import { useState } from "react";
import { FiMessageSquare, FiCheckCircle, FiClock, FiPaperclip, FiDownload } from "react-icons/fi";
import DOMPurify from "dompurify";
import { cn } from "utils/helpers";
import Modal from "newui/components/ui/Modal/Modal";
import { ClarificationChatPanel } from "newui/features/Rfx/Clarifications/components/ClarificationChat/ClarificationChatPanel";
import { formatDate } from "../../../utils/formatDate";
import type { SupplierClarificationItem } from "../../../types/scoring.type";

interface ClarificationPaneProps {
	clarifications: SupplierClarificationItem[];
	rfxId: number;
}

export const ClarificationPane = ({ clarifications, rfxId }: ClarificationPaneProps) => {
	const [chatClarificationId, setChatClarificationId] = useState<number | null>(null);

	return (
		<div data-testid="clarification-pane">
			<div className="flex items-center gap-2 mb-3">
				<FiMessageSquare size={16} className="text-gray-400" aria-hidden="true" />
				<h4 className="text-sm font-medium text-gray-700">Clarifications</h4>
				{clarifications.length > 0 && (
					<span className="text-xs text-gray-400">({clarifications.length})</span>
				)}
			</div>

			{clarifications.length === 0 ? (
				<div
					className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400"
					data-testid="clarification-pane-empty"
				>
					No clarifications for this supplier.
				</div>
			) : (
				<div className="space-y-3" data-testid="clarification-list">
					{clarifications.map((item) => (
						<div
							key={item.id}
							className="rounded-lg border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
							data-testid={`clarification-card-${item.id}`}
						>
							<div className="flex items-start justify-between gap-2 mb-2">
								<span className="text-xs font-medium text-gray-500">
									#{item.referenceNumber}
								</span>
								<div className="flex items-center gap-2">
									{item.hasChat && (
										<button
											type="button"
											onClick={() => setChatClarificationId(item.id)}
											className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
											title="View discussion thread"
											data-testid={`clarification-chat-btn-${item.id}`}
										>
											<FiMessageSquare size={11} />
											View Chat
										</button>
									)}
									<span
										className={cn(
											"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
											item.isComplete
												? "bg-green-100 text-green-700"
												: "bg-amber-100 text-amber-700"
										)}
									>
										{item.isComplete ? (
											<FiCheckCircle size={12} aria-hidden="true" />
										) : (
											<FiClock size={12} aria-hidden="true" />
										)}
										{item.isComplete ? "Complete" : "Pending"}
									</span>
								</div>
							</div>

							<div
								className="prose prose-sm max-w-none text-gray-700 mb-2 [&>p]:m-0 [&>p]:mb-1"
								dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.clarificationText) }}
							/>

							{item.relatesTo && (
								<p className="text-xs text-gray-500 mb-2">
									<span className="font-medium">Relates to:</span> {item.relatesTo}
								</p>
							)}

							{item.dateSent && (
								<p className="text-xs text-gray-400 mb-2">
									Sent: {formatDate(item.dateSent)}
								</p>
							)}

							{item.files.length > 0 && (
								<div className="mt-2 space-y-1" data-testid={`clarification-files-${item.id}`}>
									<p className="text-xs font-medium text-gray-500 flex items-center gap-1">
										<FiPaperclip size={11} /> Attachments
									</p>
									{item.files.map((file) => (
										<div key={file.id} className="flex items-center gap-2">
											{file.downloadUrl ? (
												<a
													href={file.downloadUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
													data-testid={`clarification-file-${file.id}`}
												>
													<FiDownload size={11} />
													{file.fileName}
												</a>
											) : (
												<span className="text-xs text-gray-500">{file.fileName}</span>
											)}
										</div>
									))}
								</div>
							)}

							{item.finalResponse && (
								<div className="mt-2 rounded-md bg-blue-50 p-2.5">
									<p className="text-xs font-medium text-blue-700 mb-1">Response</p>
									<div
										className="prose prose-sm max-w-none text-blue-900 [&>p]:m-0 [&>p]:mb-1"
										dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.finalResponse) }}
									/>
								</div>
							)}

							{item.impact && (
								<p className="mt-2 text-xs text-gray-500">
									<span className="font-medium">Impact:</span> {item.impact}
								</p>
							)}
						</div>
					))}
				</div>
			)}

			{chatClarificationId !== null && (
				<Modal
					isOpen
					onClose={() => setChatClarificationId(null)}
					title="Clarification Discussion"
					size="lg"
					modalType="standard"
					closeOnEsc
					closeOnOverlayClick={false}
					iconType="info"
				>
					<div className="h-[60vh]">
						<ClarificationChatPanel
							clarification={{ id: chatClarificationId, is_sent: true } as any}
							showOnlyChat
							isOpen
							rfxId={rfxId}
							readOnly
							onClose={() => setChatClarificationId(null)}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};
